import { kPositionDivisionID } from "../constants";
import { QChartTemplateRule } from "../CoreApp/QChartTemplateRule";
import { QBoxOverlay_Wand } from "../CoreApp/QBoxOverlay_Wand";
import type { QChartBox } from "../CoreApp/QChartBox";
import type { QXWSMgr } from "../CoreApp/QXWSMgr";
import { ConvertOverridesToView } from "./ConvertOverridesToView";

export const kBoxFormatCommands = {
  clearFormat: "clearOverrides",
  createRule: "createRule"
} as const;

export type BoxFormatCommand = (typeof kBoxFormatCommands)[keyof typeof kBoxFormatCommands];

export function HasBoxFormatOverrides(chartBox: QChartBox): boolean {
  return !!chartBox.GetLayout()?.HasOverrides();
}

function clearFormat(wsMgr: QXWSMgr, chartBox: QChartBox) {
  const layout = chartBox.GetLayout();
  wsMgr.StartCheckPoint();
  layout.ClearOverrides();
  wsMgr.GetDoc().GetLayoutMgr().UpdateOverrideInfo(layout);
  chartBox.RemoveOverlay(QBoxOverlay_Wand);
  wsMgr.EndCheckPoint();
  wsMgr.SetDirty();
}

// Attached at runtime by ViewManagerProvider.
type RuleEditorHost = { handleRule?: (ruleData: Record<string, unknown>) => void };

// Opens the rule editor pre-filled to apply this box's overrides to its whole department.
function createRule(wsMgr: QXWSMgr, chartBox: QChartBox): boolean {
  const layout = chartBox.GetLayout();
  const dbObj = chartBox.GetDataObj();
  const doc = chartBox.fParentPanel?.fDoc;
  const layoutMgr = doc?.GetLayoutMgr();
  const ruleEditorHost = wsMgr.GetChartTemplatesMgr() as RuleEditorHost | null;
  if (!dbObj || !doc || !layoutMgr || !ruleEditorHost?.handleRule) return false;

  const rule = new QChartTemplateRule();
  rule.InitChartTemplateRule();
  let departmentField = dbObj.GetFieldDefByID("Division");
  if (doc.IsPositionDocument() && dbObj.IsPositionObj()) {
    departmentField = dbObj.GetFieldDefByID(kPositionDivisionID);
  }
  if (!departmentField) {
    rule.AddBlankRulePair();
  } else {
    rule.AddRulePair(
      departmentField.GetFieldName(),
      "Division",
      departmentField.GetFieldClassName(),
      "equals",
      dbObj.GetFieldValue(departmentField) ?? ""
    );
  }

  layoutMgr.ConvertOverridesToRuleAction(layout, rule, dbObj.IsPositionObj());
  ruleEditorHost.handleRule({
    addNew: true,
    index: 0,
    rule,
    view: doc.GetSelfChartRules(),
    onClose: ConvertOverridesToView,
    layout,
    doc
  });
  return true;
}

export function ExecuteBoxFormatCommand(
  wsMgr: QXWSMgr,
  chartBox: QChartBox,
  cmdCode: string
): boolean {
  switch (cmdCode) {
    case kBoxFormatCommands.clearFormat:
      clearFormat(wsMgr, chartBox);
      return true;
    case kBoxFormatCommands.createRule:
      return createRule(wsMgr, chartBox);
    default:
      return false;
  }
}
